/* eslint-disable react-hooks/rules-of-hooks */
import * as Yup from 'yup';
import { ExtraIgnoreItem, FieldMappingInput, FieldMappingResponseDto } from "@/gen/api/governance";
import { FormState, FormUtils } from "@/utils/zustand-form"
import { usePlainStateFormUtils } from '@/components/input/usePlainStateFormUtils';
import { MaskingRule } from '@/gen/api/encrypt';
import { decideDataTypeMaskingKind, integerRangeCheck, nonIntegerRangeCheck } from '@/components/governance/fieldEdit/maskingRuleDataTypesMap';


interface VCtx {
  fieldDto?: FieldMappingResponseDto;
  states: FormState<FieldMappingInput, FieldMappingResponseDto>;
}
interface VTest<T> { 
  (this: Yup.TestContext<VCtx>, value: T|undefined): true | Yup.ValidationError;
}

export const fieldValidationSchema =
  Yup.object().shape({
    maskingValue: Yup.string().test(
      'dynamic-mv', 
      '', 
      function (value) {
        const { fieldDto } = (this.options.context ?? {}) as VCtx
        if (!fieldDto) return true;
        const maskingKind = decideDataTypeMaskingKind(fieldDto.dataType || '')
        const maskingRule: number = this.resolve(Yup.ref('maskingRule'))
        const maskingValue = value || ''
        
        if (maskingRule !== MaskingRule.None) {
          if (value === '') return new Yup.ValidationError('Masking Value is required', value, 'maskingValue')
          if (maskingKind === 'date') {
            if (!/\d{4}-\d{2}-\d{2}$/.test(maskingValue.trim())
            || isNaN(Date.parse(maskingValue.trim()))
            ) {
              return new Yup.ValidationError('Invalid date string', value, 'maskingValue')
            }
          }
          if (maskingKind === 'integer_num') {
            const mv = maskingValue.trim()
            if (!/^-?\d+$/.test(mv)) {
              return new Yup.ValidationError('Invalid masking value', value, 'maskingValue')
            } else if (!integerRangeCheck(mv, fieldDto.dataType || '')) {
              return new Yup.ValidationError('Invalid masking value (numeric range)', value, 'maskingValue')
            }
          }
          if (maskingKind === 'non_integer_num') {
            const mv = maskingValue.trim()
            const i = mv.indexOf('.')
            if (isNaN(Number.parseFloat(mv)) 
            || (i > -1 && !/^\d*$/.test(mv.substring(i + 1)))
            || (i === -1 && !/^-?\d+(.\d+)?$/.test(mv))
            ) {
              return new Yup.ValidationError('Invalid masking value', value, 'maskingValue')
            } else if (!nonIntegerRangeCheck(mv, fieldDto.dataType || '', fieldDto.precision, fieldDto.scale)) {
              return new Yup.ValidationError('Invalid masking value (numeric range or scale)', value, 'maskingValue')
            }
          }
        }
        return true
      } as VTest<string>),
    maskingRule: Yup.number().nullable(true).transform((value, rawValue) => rawValue === '' ? undefined : value).test(
      'dynamic-mr', 
      '', 
      function (value) {
        const { fieldDto } = (this.options.context ?? {}) as VCtx
        if (!fieldDto) return true;
        const skipMappingValidate = !!(fieldDto.isIdentity || fieldDto.isComputed || fieldDto.extraIgnoreItem !== ExtraIgnoreItem.None)
        if (((value as any) === '' || value == null) && !skipMappingValidate) return new Yup.ValidationError('Masking Rule is required', value, 'maskingRule')
        return true;
      }  
    ),
    secondaryClassificationId: Yup.number().nullable(true).test(
      'dynamic-clss',
      '',
      function(value) {
        const maskingRule: number = this.resolve(Yup.ref('maskingRule'))
        const encryptionRuleId: number = this.resolve(Yup.ref('encryptionRuleId'))
        const isSkipped: number = this.resolve(Yup.ref('isSkipped'))
        if (!value && (encryptionRuleId || !isSkipped || maskingRule)) {
          return new Yup.ValidationError('Classification is required', value, 'secondaryClassificationId')
        }
        return true
      }
    ),
    encryptionRuleId: Yup.number().nullable(true).test(
      'dynamic-erule',
      '',
      function(value) {
        const { fieldDto } = (this.options.context ?? {}) as VCtx
        if (!fieldDto) return true;
        const skipMappingValidate = !!(fieldDto.isIdentity || fieldDto.isComputed || fieldDto.extraIgnoreItem !== ExtraIgnoreItem.None)
        const isSkipped: number = this.resolve(Yup.ref('isSkipped'))
        if (!isSkipped && !value && !skipMappingValidate) {
          return new Yup.ValidationError('Encryption Rule is required', value, 'encryptionRuleId')
        }
        return true
      }
    ),
    isSkipped: Yup.boolean(),
  })

export type FieldFormUtils = 
  FormUtils<FieldMappingInput, FieldMappingResponseDto, object> & {
    resetStates: () => void;
  }

type PlainStateUtils = ReturnType<typeof usePlainStateFormUtils<FormState<FieldMappingInput, FieldMappingResponseDto> & object>>

export interface ZUtils {
  utils: FieldFormUtils;
  plainStateUtils: PlainStateUtils;
}
